import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { NAZIVI_RAVNI, usePravica, type Podrocje, type Raven } from '../seja/pravice';
import { seja } from '../seja/seja';
import { barve } from '../tema';

type Vloga = Shema['VlogaPodatki'];
type PodrocjeOpis = Shema['PodrocjePravic'];

const RAVNI: Raven[] = ['BREZ', 'PREGLED', 'UREJANJE'];

/**
 * Vloge in pravice (Nastavitve → Vloge in pravice): seznam vlog z ozirom na število uporabnikov, urejanje imena, opisa
 * in ravni na vsakem področju paketa (Brez / Pregled / Urejanje), nova vloga, kopija in brisanje vloge brez
 * uporabnikov. Skrbnik ima vse pravice in se ne ureja. Pravila (npr. analize zahtevajo nabavne cene) preveri strežnik –
 * napake so ob področju.
 */
export function Vloge() {
  const odjemalec = useQueryClient();
  const ureja = usePravica('UPORABNIKI', 'UREJANJE');
  const pregled = useQuery({
    queryKey: ['vloge'],
    queryFn: ({ signal }) => podatki(api.GET('/api/vloge', { signal })),
  });
  const [v, setV] = useState<Vloga | null>(null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  const [brisanje, setBrisanje] = useState(false);

  if (!pregled.data) {
    return (
      <StanjeNalaganja
        napaka={pregled.error}
        ponovi={() => void pregled.refetch()}
        naslov="Vlog ni bilo mogoče naložiti"
      />
    );
  }
  const vloge = pregled.data.vloge ?? [];
  const podrocja = pregled.data.podrocja ?? [];

  const izberi = (x: Vloga | null) => {
    setV(x);
    setPolja({});
    setNapaka(null);
  };

  const izvedba = async (kdo: string, dejanje: () => Promise<void>) => {
    setNapaka(null);
    setPolja({});
    setDela(kdo);
    try {
      await dejanje();
      await odjemalec.invalidateQueries({ queryKey: ['vloge'] });
      await odjemalec.invalidateQueries({ queryKey: ['uporabniki'] });
      // morda je spremenjena vloga prijavljenega uporabnika
      void seja.osveziPravice();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const shrani = () =>
    izvedba('shrani', async () => {
      if (!v) {
        return;
      }
      const shranjena = v.id
        ? await podatki(api.PUT('/api/vloge/{id}', { params: { path: { id: v.id } }, body: v }))
        : await podatki(api.POST('/api/vloge', { body: v }));
      setV(shranjena);
      notifications.show({ color: 'green', message: `Vloga »${shranjena.naziv ?? ''}« je shranjena.` });
    });

  const izbrisi = () =>
    izvedba('brisi', async () => {
      await izvedi(api.DELETE('/api/vloge/{id}', { params: { path: { id: v!.id! } } }));
      setBrisanje(false);
      notifications.show({ color: 'green', message: `Vloga »${v?.naziv ?? ''}« je izbrisana.` });
      setV(null);
    });

  const urejanje = ureja && !!v && !v.skrbnik;

  return (
    <Stack gap="md">
      <Kartica
        naslov={`Vloge (${vloge.length})`}
        dejanja={
          ureja && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => izberi({ naziv: '', pravice: {} })}
            >
              Nova vloga
            </Button>
          )
        }
      >
        <Table.ScrollContainer minWidth={300}>
          <Table highlightOnHover verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Vloga</Table.Th>
                <Table.Th visibleFrom="sm">Opis</Table.Th>
                <Table.Th ta="right">Uporabnikov</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vloge.map((x) => (
                <Table.Tr
                  key={x.id}
                  onClick={() => izberi(x)}
                  style={{ cursor: 'pointer', background: x.id === v?.id ? barve.izbrano : undefined }}
                >
                  <Table.Td fw={600}>
                    <Group gap={6} wrap="nowrap">
                      {x.naziv}
                      {x.skrbnik && (
                        <Badge size="xs" variant="light" style={{ flexShrink: 0, overflow: 'visible' }}>
                          vse pravice
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td visibleFrom="sm">
                    <Text size="sm" c="dimmed" lineClamp={2}>
                      {x.opis}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">{x.uporabnikov}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Kartica>
      {v && (
        <Kartica naslov={v.id ? `Vloga ${v.naziv ?? ''}` : 'Nova vloga'}>
          <Stack gap="sm">
            <Group grow align="flex-start" wrap="wrap">
              <TextInput
                label="Ime vloge"
                withAsterisk
                value={v.naziv ?? ''}
                onChange={(e) => setV({ ...v, naziv: e.currentTarget.value })}
                error={polja.naziv}
                disabled={!urejanje}
                miw={200}
              />
              <TextInput
                label="Opis"
                value={v.opis ?? ''}
                onChange={(e) => setV({ ...v, opis: e.currentTarget.value })}
                error={polja.opis}
                disabled={!urejanje}
                placeholder="npr. prodaja vozil brez nabavnih cen"
                miw={200}
              />
            </Group>
            {v.skrbnik && (
              <Alert variant="light" color="blue">
                Skrbnik ima vedno vse pravice, tudi na področjih, ki jih dodamo kasneje. Te vloge ni mogoče spremeniti.
                Podjetje ima vedno vsaj enega skrbnika.
              </Alert>
            )}
            <Stack gap={0}>
              {podrocja.map((p) => (
                <VrsticaPodrocja
                  key={p.podrocje}
                  podrocje={p}
                  raven={(v.pravice?.[p.podrocje as Podrocje] as Raven | undefined) ?? 'BREZ'}
                  napaka={polja[`pravice.${p.podrocje ?? ''}`]}
                  onemogoceno={!urejanje}
                  nastavi={(r) => setV({ ...v, pravice: { ...v.pravice, [p.podrocje as Podrocje]: r } })}
                />
              ))}
            </Stack>
            {napaka && (
              <Alert color="red" variant="light" role="alert">
                {napaka}
              </Alert>
            )}
            {ureja && (
              <Group justify="space-between" gap="xs">
                <Group gap="xs">
                  {v.id && (
                    <Button
                      variant="default"
                      leftSection={<IconCopy size={16} />}
                      onClick={() =>
                        izberi({
                          naziv: `${v.naziv ?? ''} (kopija)`,
                          opis: v.opis,
                          pravice: v.skrbnik ? {} : { ...v.pravice },
                        })
                      }
                    >
                      Kopiraj
                    </Button>
                  )}
                  {v.id && !v.skrbnik && (
                    <Button
                      variant="subtle"
                      color="red"
                      leftSection={<IconTrash size={16} />}
                      onClick={() => setBrisanje(true)}
                    >
                      Izbriši
                    </Button>
                  )}
                </Group>
                {urejanje && (
                  <Group gap="xs">
                    <Button variant="subtle" onClick={() => izberi(null)}>
                      Prekliči
                    </Button>
                    <Button onClick={() => void shrani()} loading={dela === 'shrani'}>
                      {v.id ? 'Shrani' : 'Dodaj'}
                    </Button>
                  </Group>
                )}
              </Group>
            )}
          </Stack>
        </Kartica>
      )}
      <Modal opened={brisanje} onClose={() => setBrisanje(false)} title="Izbriši vlogo" radius="lg">
        <Text size="sm">
          {v?.uporabnikov
            ? `Vlogo »${v.naziv ?? ''}« ima ${v.uporabnikov} uporabnikov. Najprej jim v zavihku Uporabniki izberite drugo vlogo.`
            : `Vloga »${v?.naziv ?? ''}« bo izbrisana. Želite nadaljevati?`}
        </Text>
        <Group justify="flex-end" mt="md" gap="xs">
          <Button variant="subtle" onClick={() => setBrisanje(false)}>
            Prekliči
          </Button>
          {!v?.uporabnikov && (
            <Button color="red" onClick={() => void izbrisi()} loading={dela === 'brisi'}>
              Izbriši
            </Button>
          )}
        </Group>
      </Modal>
    </Stack>
  );
}

/** Področje: ime, kaj omogočata pregled in urejanje, izbira ravni; na telefonu izbira pod imenom. */
function VrsticaPodrocja({
  podrocje,
  raven,
  napaka,
  onemogoceno,
  nastavi,
}: {
  podrocje: PodrocjeOpis;
  raven: Raven;
  napaka?: string;
  onemogoceno: boolean;
  nastavi: (r: Raven) => void;
}) {
  return (
    <Box py="xs" style={{ borderTop: `1px solid ${barve.obroba}` }}>
      <Group justify="space-between" align="flex-start" gap="xs">
        <Box style={{ flex: '1 1 260px', minWidth: 0 }}>
          <Text size="sm" fw={600}>
            {podrocje.naziv}
          </Text>
          <Text size="xs" c="dimmed">
            Pregled: {podrocje.opisPregled}
          </Text>
          <Text size="xs" c="dimmed">
            Urejanje: {podrocje.opisUrejanje}
          </Text>
        </Box>
        <SegmentedControl
          size="xs"
          aria-label={`Pravica: ${podrocje.naziv ?? ''}`}
          value={raven}
          onChange={(r) => nastavi(r as Raven)}
          disabled={onemogoceno}
          data={RAVNI.map((r) => ({ value: r, label: NAZIVI_RAVNI[r] }))}
          color={raven === 'BREZ' ? 'gray' : raven === 'PREGLED' ? 'blue' : 'green'}
        />
      </Group>
      {napaka && (
        <Text size="xs" c="red" mt={4} role="alert">
          {napaka}
        </Text>
      )}
    </Box>
  );
}
